/**
 * Settings for one match — team identity and badges, date, first-half
 * direction, video sync times, pitch and jersey colors — opened from the
 * gear on Match Administration's Matches tab. Manage Games' dialog, moved
 * here when that page folded into Match Administration (Ben, 2026-10-16),
 * with a summary of what is already set at the top.
 *
 * The match is read from GET /api/v1/admin/games (club + code: the code is
 * not unique across clubs). Saves go where they always did: the admin games
 * PATCH for the game_files fields, the facts PATCH for team identity, each
 * only when something it owns changed.
 *
 * Platform admins also get a danger zone at the bottom: Delete match
 * (DeleteMatch, below).
 */
import { Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import { formatVideoSeconds, parseVideoTime } from '../lib/videoTime'
import TeamBadges from './TeamBadges'
import '../pages/manage-games.css'
import '../pages/admin.css'

export type AdminGame = {
  /** The game_files row: what the settings PATCH addresses. */
  id: number
  /** The games row: what Delete match addresses. */
  game_id: number
  game_code: string
  org_id: number
  org_code: string
  org_name: string | null
  org_logo_url: string | null
  match_date: string | null
  has_video: boolean
  total_frames: number | null
  fps: number | null
  /** Video sync times, in seconds into the VIDEO file. */
  video_game_start_time: number | null
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_normaltime_end_time: number | null
  /** Direction the home team attacks in the first half. */
  home_team_start_direction: 'left' | 'right' | null
  /** Jersey colors as '#rrggbb'. */
  home_team_color: string | null
  away_team_color: string | null
  /** Team identity — the one fact nothing in the tracking can supply. */
  home_team_name: string | null
  away_team_name: string | null
  org_side: 'home' | 'away' | null
  tracking_home_index: 0 | 1 | null
  identity_source: string | null
  /** Pitch geometry from game_facts, in centimeters. */
  pitch_length_cm: number | null
  pitch_width_cm: number | null
  pitch_source: string | null
  /** What AWAY@HOME implies. A convention, offered, never stored. */
  suggested_home: string | null
  suggested_away: string | null
  /** Where this match's objects are looked up. */
  r2_prefix: string | null
}

/** What is already recorded for the match, in words, for the top of the
 *  dialog: set / not set for each group, with the value where short. */
export function alreadySet(g: AdminGame): { label: string; value: string | null }[] {
  const t = (s: number | null) => (s == null ? null : formatVideoSeconds(s))
  const marks = [g.video_game_start_time, g.video_halftime_start_time,
                 g.video_halftime_end_time, g.video_normaltime_end_time]
  const nSet = marks.filter((m) => m != null).length
  return [
    { label: 'Teams', value: g.home_team_name && g.away_team_name
      ? `${g.away_team_name} at ${g.home_team_name}${g.identity_source ? ` (${g.identity_source})` : ''}`
      : null },
    { label: 'Club side', value: g.org_side },
    { label: 'Date', value: g.match_date?.slice(0, 10) ?? null },
    { label: '1H direction', value: g.home_team_start_direction
      ? `home attacks ${g.home_team_start_direction}` : null },
    { label: 'Timings', value: nSet === 0 ? null
      : `${nSet} of 4 set — kick-off ${t(g.video_game_start_time) ?? '—'}, `
        + `half-time ${t(g.video_halftime_start_time) ?? '—'}–${t(g.video_halftime_end_time) ?? '—'}, `
        + `end ${t(g.video_normaltime_end_time) ?? '—'}` },
    { label: 'Pitch', value: g.pitch_length_cm && g.pitch_width_cm
      ? `${(g.pitch_length_cm / 100).toFixed(1)} × ${(g.pitch_width_cm / 100).toFixed(1)} m`
        + (g.pitch_source ? ` (${g.pitch_source})` : '')
      : null },
    { label: 'Colors', value: g.home_team_color || g.away_team_color
      ? `home ${g.home_team_color ?? 'not set'}, away ${g.away_team_color ?? 'not set'}`
      : null },
  ]
}

/**
 * Deleting a match — platform admins only (Ben, 2026-10-23: test uploads
 * on a club). What it removes and what it keeps are said before it is
 * offered; the code typed back and a reason are enforced by the server too
 * (DELETE /api/v1/admin/matches/<games.id>), so neither is a courtesy of this
 * form. Keyed on the games row, not game_files: a match can have the first
 * without the second, and still has to be deletable (2026-10-23).
 */
export function DeleteMatch({ game, onDeleted }: {
  game: Pick<AdminGame, 'game_id' | 'game_code'>
    & Partial<Pick<AdminGame, 'org_code' | 'org_name'>>
  onDeleted: () => void
}) {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const armed = typed.trim() === game.game_code && why.trim().length > 0

  const remove = async () => {
    setBusy(true); setErr(null)
    try {
      await api(`/api/v1/admin/matches/${game.game_id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason: why.trim(), confirm_code: typed.trim() }),
      })
      onDeleted()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <fieldset className="mg-cfg-section">
      <legend className="mg-cfg-legend">Delete match</legend>
      <div className="adm-danger">
        <p className="adm-note">
          Removes {game.game_code} from {game.org_name ?? game.org_code ?? 'this club'}: the
          match, its processing jobs, labels and every table of analytics
          for it. The video and tracking files in storage are{' '}
          <strong>not</strong> touched, so it can be re-imported. This cannot
          be undone from here.
        </p>
        {!open && (
          <button className="adm-btn adm-btn--danger" onClick={() => setOpen(true)}>
            <Trash2 size={13} /> Delete match…
          </button>
        )}
      </div>
      {open && (
        <>
          <label className="adm-field">
            <span className="t-label">Type <code>{game.game_code}</code> to confirm</span>
            <input className="adm-input" value={typed} autoFocus
                   aria-label="Type the match code to confirm"
                   onChange={(e) => setTyped(e.target.value)} />
          </label>
          <label className="adm-field">
            <span className="t-label">Reason (required)</span>
            <input className="adm-input" value={why}
                   aria-label="Reason for deleting"
                   placeholder="e.g. test upload"
                   onChange={(e) => setWhy(e.target.value)} />
          </label>
          {err && <p className="adm-note mg-ht-error" role="alert">{err}</p>}
          <div className="adm-actions">
            <button className="adm-btn adm-btn--danger"
                    disabled={!armed || busy}
                    onClick={() => void remove()}>
              {busy ? 'Deleting…' : `Delete ${game.game_code} permanently`}
            </button>
            <button className="adm-btn adm-btn--quiet" disabled={busy}
                    onClick={() => { setOpen(false); setTyped(''); setWhy(''); setErr(null) }}>
              Cancel
            </button>
          </div>
        </>
      )}
    </fieldset>
  )
}

export default function MatchSettingsDialog({ orgId, gameCode, gameId, canUploadBadges,
                                               canDelete = false,
                                               onClose, onSaved, onDeleted }: {
  orgId: number
  gameCode: string
  /** The games row. With it, a match that has no upload record (no
   *  game_files row, so no settings to edit) can still be deleted. */
  gameId?: number
  canUploadBadges: boolean
  /** Platform admins: the Delete match danger zone. */
  canDelete?: boolean
  onClose: () => void
  onSaved?: () => void
  /** After a delete: the match is gone, so the caller drops it. */
  onDeleted?: () => void
}) {
  const [g, setG] = useState<AdminGame | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // No game_files row: there are no settings, but the match exists
  const [noUpload, setNoUpload] = useState(false)
  const [cfgDate, setCfgDate] = useState('')
  const [cfgStart, setCfgStart] = useState('')
  const [cfgHtStart, setCfgHtStart] = useState('')
  const [cfgHtEnd, setCfgHtEnd] = useState('')
  const [cfgEnd, setCfgEnd] = useState('')
  const [cfgDir, setCfgDir] = useState<'' | 'left' | 'right'>('')
  const [cfgHome, setCfgHome] = useState('')
  const [cfgAway, setCfgAway] = useState('')
  const [cfgSide, setCfgSide] = useState<'' | 'home' | 'away'>('')
  const [cfgIdx, setCfgIdx] = useState<'0' | '1'>('1')
  const [cfgPitchL, setCfgPitchL] = useState('')
  const [cfgPitchW, setCfgPitchW] = useState('')
  const [cfgHomeColor, setCfgHomeColor] = useState('')
  const [cfgAwayColor, setCfgAwayColor] = useState('')
  const [cfgReason, setCfgReason] = useState('')
  const [cfgError, setCfgError] = useState<string | null>(null)
  const [cfgSaving, setCfgSaving] = useState(false)

  useEffect(() => {
    let on = true
    const qs = new URLSearchParams({ q: gameCode, org: String(orgId) })
    api<{ games: AdminGame[] }>(`/api/v1/admin/games?${qs.toString()}`)
      .then((d) => {
        if (!on) return
        const row = d.games.find((x) => x.game_code === gameCode && x.org_id === orgId)
        if (!row) {
          if (gameId != null) setNoUpload(true)
          else setLoadError(`No game ${gameCode} in this club.`)
          return
        }
        setG(row)
        setCfgDate(row.match_date?.slice(0, 10) ?? '')
        const t = (s: number | null) => (s != null ? formatVideoSeconds(s) : '')
        setCfgStart(t(row.video_game_start_time))
        setCfgHtStart(t(row.video_halftime_start_time))
        setCfgHtEnd(t(row.video_halftime_end_time))
        setCfgEnd(t(row.video_normaltime_end_time))
        setCfgDir(row.home_team_start_direction ?? '')
        setCfgHome(row.home_team_name ?? row.suggested_home ?? '')
        setCfgAway(row.away_team_name ?? row.suggested_away ?? '')
        const oc = row.org_code?.toUpperCase()
        const sh = row.suggested_home?.toUpperCase()
        const sa = row.suggested_away?.toUpperCase()
        const guess: '' | 'home' | 'away' =
          oc && sh && oc === sh ? 'home' : oc && sa && oc === sa ? 'away' : ''
        setCfgSide(row.org_side ?? guess)
        setCfgIdx(row.tracking_home_index === 0 ? '0' : '1')
        setCfgPitchL(row.pitch_length_cm != null ? String(row.pitch_length_cm) : '')
        setCfgPitchW(row.pitch_width_cm != null ? String(row.pitch_width_cm) : '')
        setCfgHomeColor(row.home_team_color ?? '')
        setCfgAwayColor(row.away_team_color ?? '')
      })
      .catch((e: unknown) => {
        if (on) setLoadError(e instanceof ApiError ? e.message : String(e))
      })
    return () => { on = false }
  }, [orgId, gameCode, gameId])

  async function saveSettings() {
    if (!g) return
    setCfgError(null)
    setCfgSaving(true)
    const start = parseVideoTime(cfgStart)
    const htStart = parseVideoTime(cfgHtStart)
    const htEnd = parseVideoTime(cfgHtEnd)
    const end = parseVideoTime(cfgEnd)
    const fail = (m: string) => { setCfgError(m); setCfgSaving(false) }
    if ([start, htStart, htEnd, end].some((v) => Number.isNaN(v)))
      return fail('Sync times must be mm:ss (or h:mm:ss), or raw seconds.')
    const colorRe = /^#[0-9a-fA-F]{6}$/
    if (cfgHomeColor && !colorRe.test(cfgHomeColor))
      return fail('Home jersey color must be #rrggbb hex.')
    if (cfgAwayColor && !colorRe.test(cfgAwayColor))
      return fail('Away jersey color must be #rrggbb hex.')
    const pitchL = cfgPitchL.trim() ? parseInt(cfgPitchL, 10) : null
    const pitchW = cfgPitchW.trim() ? parseInt(cfgPitchW, 10) : null
    if (cfgPitchL.trim() && (isNaN(pitchL!) || pitchL! <= 0))
      return fail('Pitch length must be a positive number (centimeters).')
    if (cfgPitchW.trim() && (isNaN(pitchW!) || pitchW! <= 0))
      return fail('Pitch width must be a positive number (centimeters).')
    let partly = false
    try {
      // Only changed fields: an unconditional body stamps no-op audit rows
      // that bury the real change.
      const patch: Record<string, unknown> = { reason: cfgReason.trim() }
      const newDate = cfgDate.trim() || null
      if (newDate !== (g.match_date?.slice(0, 10) ?? null)) patch.match_date = newDate
      if (start !== g.video_game_start_time) patch.video_game_start_time = start
      if (htStart !== g.video_halftime_start_time) patch.video_halftime_start_time = htStart
      if (htEnd !== g.video_halftime_end_time) patch.video_halftime_end_time = htEnd
      if (end !== g.video_normaltime_end_time) patch.video_normaltime_end_time = end
      if ((cfgDir || null) !== g.home_team_start_direction)
        patch.home_team_start_direction = cfgDir || null
      if ((cfgHomeColor || null) !== g.home_team_color)
        patch.home_team_color = cfgHomeColor || null
      if ((cfgAwayColor || null) !== g.away_team_color)
        patch.away_team_color = cfgAwayColor || null
      if (pitchL !== g.pitch_length_cm || pitchW !== g.pitch_width_cm) {
        patch.pitch_length_cm = pitchL
        patch.pitch_width_cm = pitchW
      }
      if (Object.keys(patch).length > 1) {
        await api(`/api/v1/admin/games/${g.id}`, {
          method: 'PATCH', body: JSON.stringify(patch) })
        // what is saved is now the baseline: a retry after the identity
        // save fails re-sends only the identity, not these fields again
        const { reason: _r, ...saved } = patch
        setG((prev) => (prev ? { ...prev, ...saved } as AdminGame : prev))
        partly = true
      }
      // Team identity: the facts route, its own provenance.
      const identityChanged =
        cfgHome.trim() !== (g.home_team_name ?? '') ||
        cfgAway.trim() !== (g.away_team_name ?? '') ||
        cfgSide !== (g.org_side ?? '') ||
        Number(cfgIdx) !== (g.tracking_home_index ?? 1)
      if (identityChanged && cfgHome.trim() && cfgAway.trim()) {
        await api(`/api/v1/games/${pathSeg(g.game_code)}/facts?org_id=${g.org_id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            home_team_name: cfgHome.trim(), away_team_name: cfgAway.trim(),
            org_side: cfgSide, tracking_home_index: Number(cfgIdx),
            reason: cfgReason.trim(),
          }),
        })
      }
      onSaved?.()
      onClose()
    } catch (e: unknown) {
      // the first save landed: the list should show it even if this failed
      if (partly) onSaved?.()
      setCfgError((e as { message?: string }).message || 'Could not save settings.')
    } finally {
      setCfgSaving(false)
    }
  }

  return (
    <div className="mg-scrim" role="dialog" aria-modal="true"
         aria-label={`Settings for ${gameCode}`}
         onClick={(e) => e.stopPropagation()}>
      <div className="panel mg-settings-dialog">
        <div className="mg-settings-header">
          <h3 className="mg-confirm__title">
            Settings for {gameCode}
            {g && <span className="muted"> — {g.org_name ?? g.org_code}</span>}
          </h3>
          <button className="mg-settings-close" onClick={onClose}
                  aria-label="Close"><X size={16} /></button>
        </div>
        {loadError && <p className="adm-note mg-ht-error" role="alert">{loadError}</p>}
        {!g && !loadError && !noUpload && <p className="muted">Loading&hellip;</p>}
        {noUpload && gameId != null && (
          <>
            <p className="adm-note" data-testid="no-upload">
              {gameCode} has no upload record (no game_files row), so there
              are no settings to edit here.
            </p>
            {canDelete && (
              <DeleteMatch game={{ game_id: gameId, game_code: gameCode }}
                           onDeleted={() => { onDeleted?.(); onClose() }} />
            )}
          </>
        )}
        {g && (
      <>
        {/* ── Already set ──────────────────────────────────────── */}
        <fieldset className="mg-cfg-section" data-testid="already-set">
          <legend className="mg-cfg-legend">Already set</legend>
          <dl className="mg-cfg-set">
            {alreadySet(g).map((r) => (
              <div key={r.label} className="mg-cfg-set__row">
                <dt className="t-label">{r.label}</dt>
                <dd className={r.value ? '' : 'muted'}>
                  {r.label === 'Colors' && r.value ? (
                    <>
                      <span className="mg-swatch" style={{ background: g.home_team_color ?? 'transparent' }} />
                      <span className="mg-swatch" style={{ background: g.away_team_color ?? 'transparent' }} />
                      {r.value}
                    </>
                  ) : (r.value ?? 'not set')}
                </dd>
              </div>
            ))}
          </dl>
          {g.r2_prefix && (
            <p className="muted mg-prefix__note">
              Storage prefix <code className="mg-prefix">{g.r2_prefix}/</code>
            </p>
          )}
        </fieldset>

        {/* ── Team badges (logos) ──────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Team badges</legend>
          <TeamBadges orgId={g.org_id} gameCode={g.game_code}
                      canUpload={canUploadBadges} />
        </fieldset>

        {/* ── Team identity ────────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Team identity</legend>
          <p className="adm-note">
            Which club is home and which is away. The game code
            reads {g.suggested_away} at {g.suggested_home} —
            check it; the code is a naming convention, not a record.
          </p>
          <div className="mg-cfg-row">
            <label className="adm-field">
              <span className="t-label">Home team</span>
              <input className="adm-input" value={cfgHome}
                     placeholder="e.g. University of Florida"
                     onChange={(e) => setCfgHome(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Away team</span>
              <input className="adm-input" value={cfgAway}
                     placeholder="e.g. UCF Women"
                     onChange={(e) => setCfgAway(e.target.value)} />
            </label>
          </div>
          <div className="mg-cfg-row">
            <div className="adm-field">
              <span className="t-label">{g.org_code}&rsquo;s side</span>
              <label className="mg-radio">
                <input type="radio" name="cfg-side" value="home"
                       checked={cfgSide === 'home'}
                       onChange={() => setCfgSide('home')} />
                Home
              </label>
              <label className="mg-radio">
                <input type="radio" name="cfg-side" value="away"
                       checked={cfgSide === 'away'}
                       onChange={() => setCfgSide('away')} />
                Away
              </label>
            </div>
            <label className="adm-field">
              <span className="t-label">Home side in tracking</span>
              <select className="adm-input" value={cfgIdx}
                      onChange={(e) => setCfgIdx(e.target.value as '0' | '1')}>
                <option value="1">Team 1 (vendor convention)</option>
                <option value="0">Team 0</option>
              </select>
            </label>
          </div>
        </fieldset>

        {/* ── Match date ───────────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Match date</legend>
          <label className="adm-field">
            <span className="t-label">Date</span>
            <input className="adm-input" type="date" value={cfgDate}
                   onChange={(e) => setCfgDate(e.target.value)} />
          </label>
        </fieldset>

        {/* ── Direction ────────────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">First-half direction</legend>
          <p className="adm-note">
            Which way the home team ({cfgHome.trim() || 'home'}) attacks
            in the first half.
          </p>
          <div className="mg-cfg-row">
            <label className="mg-radio">
              <input type="radio" name="cfg-dir" value="right"
                     checked={cfgDir === 'right'}
                     onChange={() => setCfgDir('right')} />
              Attacking right &rarr;
            </label>
            <label className="mg-radio">
              <input type="radio" name="cfg-dir" value="left"
                     checked={cfgDir === 'left'}
                     onChange={() => setCfgDir('left')} />
              &larr; Attacking left
            </label>
            <label className="mg-radio">
              <input type="radio" name="cfg-dir" value=""
                     checked={cfgDir === ''}
                     onChange={() => setCfgDir('')} />
              Not set
            </label>
          </div>
        </fieldset>

        {/* ── Video sync times ─────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Video sync times</legend>
          <p className="adm-note">
            Where each moment sits in the <strong>video file</strong>,
            in mm:ss or raw seconds. Clear a box to unset that mark.
          </p>
          <div className="mg-cfg-row">
            <label className="adm-field">
              <span className="t-label">Game starts</span>
              <input className="adm-input" value={cfgStart}
                     placeholder="e.g. 0:00"
                     onChange={(e) => setCfgStart(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Halftime starts</span>
              <input className="adm-input" value={cfgHtStart}
                     placeholder="e.g. 54:27"
                     onChange={(e) => setCfgHtStart(e.target.value)} />
            </label>
          </div>
          <div className="mg-cfg-row">
            <label className="adm-field">
              <span className="t-label">Halftime ends</span>
              <input className="adm-input" value={cfgHtEnd}
                     placeholder="e.g. 71:03"
                     onChange={(e) => setCfgHtEnd(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Normal time ends</span>
              <input className="adm-input" value={cfgEnd}
                     placeholder="e.g. 116:30"
                     onChange={(e) => setCfgEnd(e.target.value)} />
            </label>
          </div>
        </fieldset>

        {/* ── Pitch dimensions ─────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Pitch dimensions</legend>
          <p className="adm-note">
            Length and width in centimeters.
            {g.pitch_source
              ? <> Current source: <strong>{g.pitch_source}</strong>.</>
              : ' Not yet recorded.'}
          </p>
          <div className="mg-cfg-row">
            <label className="adm-field">
              <span className="t-label">Length (cm)</span>
              <input className="adm-input" type="number" value={cfgPitchL}
                     placeholder="e.g. 10500"
                     onChange={(e) => setCfgPitchL(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Width (cm)</span>
              <input className="adm-input" type="number" value={cfgPitchW}
                     placeholder="e.g. 6800"
                     onChange={(e) => setCfgPitchW(e.target.value)} />
            </label>
          </div>
          {cfgPitchL && cfgPitchW && (
            <span className="muted">
              {(parseInt(cfgPitchL, 10) / 100).toFixed(1)} &times;{' '}
              {(parseInt(cfgPitchW, 10) / 100).toFixed(1)} m
            </span>
          )}
        </fieldset>

        {/* ── Jersey colors ────────────────────────────────────── */}
        <fieldset className="mg-cfg-section">
          <legend className="mg-cfg-legend">Jersey colors</legend>
          <div className="mg-cfg-row">
            <label className="adm-field">
              <span className="t-label">Home jersey ({cfgHome.trim() || 'home'})</span>
              <span className="mg-color-input">
                <input type="color" value={cfgHomeColor || '#888888'}
                       onChange={(e) => setCfgHomeColor(e.target.value)} />
                <input className="adm-input" value={cfgHomeColor}
                       placeholder="#rrggbb"
                       onChange={(e) => setCfgHomeColor(e.target.value)} />
                {cfgHomeColor && (
                  <button className="mg-color-clear" onClick={() => setCfgHomeColor('')}
                          title="Clear"><X size={12} /></button>
                )}
              </span>
            </label>
            <label className="adm-field">
              <span className="t-label">Away jersey ({cfgAway.trim() || 'away'})</span>
              <span className="mg-color-input">
                <input type="color" value={cfgAwayColor || '#888888'}
                       onChange={(e) => setCfgAwayColor(e.target.value)} />
                <input className="adm-input" value={cfgAwayColor}
                       placeholder="#rrggbb"
                       onChange={(e) => setCfgAwayColor(e.target.value)} />
                {cfgAwayColor && (
                  <button className="mg-color-clear" onClick={() => setCfgAwayColor('')}
                          title="Clear"><X size={12} /></button>
                )}
              </span>
            </label>
          </div>
        </fieldset>


        {/* ── Reason + actions ─────────────────────────────────── */}
        <label className="adm-field">
          <span className="t-label">Reason for update</span>
          <input className="adm-input" value={cfgReason}
                 onChange={(e) => setCfgReason(e.target.value)}
                 placeholder="e.g. initial setup from fixture list" />
        </label>
        {cfgError && <p className="adm-note mg-ht-error" role="alert">{cfgError}</p>}
        <div className="adm-actions">
          <button className="adm-btn" disabled={cfgSaving || !cfgReason.trim()}
                  onClick={() => void saveSettings()}>
            {cfgSaving ? 'Saving…' : 'Save all'}
          </button>
          <button className="adm-btn adm-btn--quiet" disabled={cfgSaving}
                  onClick={onClose}>Cancel</button>
        </div>

        {canDelete && (
          <DeleteMatch game={g}
                       onDeleted={() => { onDeleted?.(); onClose() }} />
        )}
      </>
        )}
      </div>
    </div>
  )
}
